import { formValue } from "../../../formServiceFunctions";
import * as stagesDocument from "../../../Stages/StagesDocument";
import { trueFalseDefaultOptions } from "../formDefinitionFunctions";
import { translate } from "../../../Translations/translatorService";
import { dateFieldDefault, textFieldDefault } from "../../../validationRules";
import addressFields from "../modules/addressFields";
import personFields from "../modules/personFields";
import basicCaseInsuranceFields from "../modules/basicCaseInsuranceFields";
import { UseForm } from "../../../../types/UseForm";
import { ProductUnion } from "../../../../types/ProductUnion";

const childrenFields = () => {
  let fields = [];

  for (let i = 0; i < 6; i++) {
    fields.push(
      {
        type: "ValidatorTextField",
        path: "alimony.children[" + i + "].name",
        label: translate("alimony.children.name.label"),
        validators: textFieldDefault,
        isHidden: ({ product }: { product: ProductUnion }) => product.numberOfChildren <= i,
      },
      {
        type: "ValidatorDateField",
        path: "alimony.children[" + i + "].birthDate",
        label: translate("alimony.children.birthDate.label"),
        isHidden: ({ product }: { product: ProductUnion }) => product.numberOfChildren <= i,
      },
      {
        type: "EmptyElement",
        //because path is also used as the key for react
        path: "empty" + i,
      },
      {
        type: "ValidatorSelect",
        path: "alimony.children[" + i + "].childOf",
        label: translate("alimony.children.childOf.label"),
        options: () => [
          { value: "petitioner", label: translate("alimony.children.childOf.values.petitioner") },
          { value: "partner", label: translate("alimony.children.childOf.values.partner") },
          { value: "both", label: translate("alimony.children.childOf.values.both") },
        ],
        isHidden: ({ product }: { product: ProductUnion }) => product.numberOfChildren <= i,
      },
      {
        type: "ValidatorSelect",
        path: "alimony.children[" + i + "].livesWith",
        label: translate("alimony.children.livesWith.label"),
        options: () => [
          { value: "petitioner", label: translate("alimony.children.livesWith.values.petitioner") },
          { value: "partner", label: translate("alimony.children.livesWith.values.partner") },
          { value: "both", label: translate("alimony.children.livesWith.values.both") },
          { value: "none", label: translate("alimony.children.livesWith.values.none") },
        ],
        isHidden: ({ product }: { product: ProductUnion }) => product.numberOfChildren <= i,
      },
      {
        type: "ValidatorSelect",
        path: "alimony.children[" + i + "].childAllowanceReceiver",
        label: translate("alimony.children.childAllowanceReceiver.label"),
        options: () => [
          { value: "petitioner", label: translate("alimony.children.childAllowanceReceiver.values.petitioner") },
          { value: "partner", label: translate("alimony.children.childAllowanceReceiver.values.partner") },
          { value: "both", label: translate("alimony.children.childAllowanceReceiver.values.both") },
          { value: "child", label: translate("alimony.children.childAllowanceReceiver.values.child") },
          { value: "none", label: translate("alimony.children.childAllowanceReceiver.values.none") },
        ],
        isHidden: ({ product }: { product: ProductUnion }) => product.numberOfChildren <= i,
      }
    );
  }
  return fields;
};

export const fullFormDefinition = (product: ProductUnion) => ({
  product: {
    sections: [
      {
        label: "Typ der Analyse",
        elements: [
          {
            type: "ValidatorSelect",
            path: `alimony.basicChoice`,
            isDisabled: () => true,
            options: () => [{ value: "__null__" }, { value: "complete" }, { value: "children" }, { value: "other" }],
          },
        ],
      },
      {
        label: "Finanzielle Situation Antragsteller",
        isHidden: ({ product }: { product: ProductUnion }) => product.basicChoice === "other",
        elements: [
          {
            type: "ValidatorSelect",
            path: "alimony.alimonyPetitioner.employmentStatus",
            options: () => [
              { value: "__null__" },
              { value: "employed" },
              { value: "selfEmployed" },
              { value: "unemployed" },
            ],
          },
          {
            type: "ValidatorNumberField",
            path: "alimony.alimonyPetitioner.netIncome",
            numberType: "float",
            fieldType: "money",
          },
          {
            type: "ValidatorNumberField",
            path: "alimony.alimonyPetitioner.otherIncome",
            numberType: "float",
            fieldType: "money",
          },
          {
            type: "ValidatorSelect",
            path: "alimony.alimonyPetitioner.livingSituation",
            options: () => [
              { value: "__null__" },
              { value: "tenant" },
              { value: "ownerWithMortgage" },
              { value: "ownerOutright" },
              { value: "other" },
            ],
          },
          {
            type: "ValidatorNumberField",
            path: "alimony.alimonyPetitioner.livingExpenses",
            numberType: "float",
            fieldType: "money",
            isHidden: ({ values }: { values: UseForm["values"] }) =>
              values.alimony.alimonyPetitioner.livingSituation !== "tenant",
          },
          {
            type: "ValidatorNumberField",
            path: "alimony.alimonyPetitioner.monthlyMortgagePayment",
            numberType: "float",
            fieldType: "money",
            isHidden: ({ values }: { values: UseForm["values"] }) =>
              values.alimony.alimonyPetitioner.livingSituation !== "ownerWithMortgage",
          },
          {
            type: "ValidatorNumberField",
            path: "alimony.alimonyPetitioner.monthlyImputedRent",
            numberType: "float",
            fieldType: "money",
            isHidden: ({ values }: { values: UseForm["values"] }) =>
              !["ownerWithMortgage", "ownerOutright", "other"].includes(
                values.alimony.alimonyPetitioner.livingSituation
              ),
          },
          {
            type: "AlimonyExpenses",
            path: "alimony.alimonyPetitioner.expensesList",
            additionalProps: {
              fullWidth: true,
            },
          },
        ],
      },
      {
        label: "Finanzielle Situation anderer Elternteil",
        isHidden: ({ product }: { product: ProductUnion }) => product.basicChoice === "other",
        elements: [
          {
            type: "ValidatorSelect",
            path: "alimony.alimonyPartner.person.gender",
            additionalProps: { displayEmpty: true },
            options: () => [{ value: "", label: "Keine Anrede" }, { value: "male" }, { value: "female" }],
          },
          {
            type: "ValidatorTextField",
            path: "alimony.alimonyPartner.person.givenName",
            validators: [...textFieldDefault],
          },
          {
            type: "ValidatorTextField",
            path: "alimony.alimonyPartner.person.familyName",
            validators: [...textFieldDefault],
          },
          {
            type: "ValidatorSelect",
            path: "alimony.alimonyPartner.employmentStatus",
            options: () => [
              { value: "__null__" },
              { value: "employed" },
              { value: "selfEmployed" },
              { value: "unemployed" },
            ],
          },
          {
            type: "ValidatorNumberField",
            path: "alimony.alimonyPartner.netIncome",
            numberType: "float",
            fieldType: "money",
          },
          {
            type: "ValidatorNumberField",
            path: "alimony.alimonyPartner.otherIncome",
            numberType: "float",
            fieldType: "money",
          },
          {
            type: "ValidatorSelect",
            path: "alimony.alimonyPartner.livingSituation",
            options: () => [
              { value: "tenant" },
              { value: "ownerWithMortgage" },
              { value: "ownerOutright" },
              { value: "other" },
            ],
          },
          {
            type: "ValidatorNumberField",
            path: "alimony.alimonyPartner.livingExpenses",
            numberType: "float",
            fieldType: "money",
            isHidden: ({ values }: { values: UseForm["values"] }) =>
              values.alimony.alimonyPartner.livingSituation !== "tenant",
          },
          {
            type: "ValidatorNumberField",
            path: "alimony.alimonyPartner.monthlyMortgagePayment",
            numberType: "float",
            fieldType: "money",
            isHidden: ({ values }: { values: UseForm["values"] }) =>
              values.alimony.alimonyPartner.livingSituation !== "ownerWithMortgage",
          },
          {
            type: "ValidatorNumberField",
            path: "alimony.alimonyPartner.monthlyImputedRent",
            numberType: "float",
            fieldType: "money",
            isHidden: ({ values }: { values: UseForm["values"] }) =>
              !["ownerWithMortgage", "ownerOutright", "other"].includes(values.alimony.alimonyPartner.livingSituation),
          },
          {
            type: "AlimonyExpenses",
            path: "alimony.alimonyPartner.expensesList",
            additionalProps: {
              fullWidth: true,
            },
          },
        ],
      },
      {
        label: "Familiäre Situation",
        isHidden: ({ product }: { product: ProductUnion }) => product.basicChoice === "other",
        elements: [
          {
            type: "ValidatorDateField",
            path: "alimony.dateMarriage",
            validators: dateFieldDefault,
          },
          {
            type: "ValidatorSelect",
            path: "alimony.areSeparated",
            options: () => trueFalseDefaultOptions("alimony.areSeparated"),
          },
          {
            type: "ValidatorDateField",
            path: "alimony.dateSeparation",
            validators: dateFieldDefault,
          },
          {
            type: "ValidatorSelect",
            path: "alimony.areDivorced",
            options: () => trueFalseDefaultOptions("alimony.areDivorced"),
          },
          {
            type: "ValidatorDateField",
            path: "alimony.dateDivorce",
            validators: dateFieldDefault,
          },
        ],
      },
      {
        label: "Angaben zu Kindern",
        isHidden: ({ values, product }: { values: UseForm["values"]; product: ProductUnion }) =>
          product.basicChoice === "other" || parseInt(formValue(values, "alimony.numberOfChildren"), 10) === 0,
        elements: childrenFields(),
      },
      {
        label: "Persönliche Angaben Mandantschaft",
        elements: [
          {
            type: "ValidatorSelect",
            path: "alimony.client.gender",
            additionalProps: { displayEmpty: true },
            options: () => [{ value: "", label: "Keine Anrede" }, { value: "male" }, { value: "female" }],
          },
          {
            type: "ValidatorTextField",
            path: "alimony.client.givenName",
            validators: [...textFieldDefault],
          },
          {
            type: "ValidatorTextField",
            path: "alimony.client.familyName",
            validators: [...textFieldDefault],
          },
          {
            type: "ValidatorTextField",
            path: "alimony.client.residenceAddress.streetAddress",
            validators: [...textFieldDefault],
          },
          {
            type: "ValidatorTextField",
            path: "alimony.client.residenceAddress.postalCode",
            validators: [...textFieldDefault],
          },
          {
            type: "ValidatorTextField",
            path: "alimony.client.residenceAddress.addressLocality",
            validators: [...textFieldDefault],
          },
        ],
      },
      {
        label: "Persönliche Angaben Antragsteller",
        elements: [
          ...personFields({
            path: "alimony.alimonyPetitioner.person",
          }),
          ...addressFields({
            path: "alimony.alimonyPetitioner.person.residenceAddress",
          }),
        ],
      },
      {
        label: "Anliegen",
        elements: [
          {
            type: "ValidatorTextField",
            path: "alimony.personalTextMessage",
            isDisabled: ({ product }: { product: ProductUnion }) => (product.stage || 0) >= stagesDocument.STAGE_READY,
            additionalProps: {
              multiline: true,
              rows: 12,
              fullWidth: true,
            },
          },
        ],
      },
    ],
  },
  additional: {
    sections: [
      {
        label: "",
        elements: [
          {
            type: "ValidatorTextField",
            path: "alimony.reference",
            isDisabled: () => true,
          },
        ],
      },
      {
        label: "Angaben zur Versicherung",
        elements: [
          ...basicCaseInsuranceFields({
            path: "alimony.caseInsurance",
            product: product,
          }),
          {
            type: "ValidatorNumberField",
            path: "alimony.caseInsurance.deductible",
            fieldType: "money",
            numberType: "float",
          },
        ],
      },
    ],
  },
});
